import { tr } from '@/contexts/LanguageContext';
// mediaUtils — utilities compartidas para la subida de archivos.
// Usadas por MediaPicker, MediaUploadField y los uploaders contextuales.

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
export const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'];

export const DEFAULT_LIMITS = {
  imageMaxMB: 10,
  videoMaxMB: 200,
  videoMaxSec: 600 // 10 min
};

export const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
};

export const formatDuration = (sec) => {
  if (!Number.isFinite(sec)) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const isImage = (file) => {
  if (!file) return false;
  if (file.type) return file.type.startsWith('image/');
  return ACCEPTED_IMAGE_TYPES.some((t) => file.name?.toLowerCase().endsWith(t.split('/')[1]));
};

export const isVideo = (file) => {
  if (!file) return false;
  if (file.type) return file.type.startsWith('video/');
  return ACCEPTED_VIDEO_TYPES.some((t) => file.name?.toLowerCase().endsWith(t.split('/')[1].replace('quicktime', 'mov')));
};

export const validateFile = (file, opts = {}) => {
  const {
    accept = 'both',           // 'image' | 'video' | 'both'
    imageMaxMB = DEFAULT_LIMITS.imageMaxMB,
    videoMaxMB = DEFAULT_LIMITS.videoMaxMB,
  } = opts;

  if (!file) return { valid: false, error: tr("No se seleccionó archivo") };
  if (file.size === 0) return { valid: false, error: tr("El archivo está vacío") };

  const img = isImage(file);
  const vid = isVideo(file);

  if (accept === 'image' && !img) return { valid: false, error: tr("Solo se permiten imágenes (JPG, PNG, WEBP)") };
  if (accept === 'video' && !vid) return { valid: false, error: tr("Solo se permiten videos (MP4, MOV, WEBM)") };
  if (accept === 'both' && !img && !vid) return { valid: false, error: tr("Formato no soportado. Usa imagen o video.") };

  const sizeMB = file.size / (1024 * 1024);
  // null / undefined / 0 = sin límite
  if (img && imageMaxMB && sizeMB > imageMaxMB) return { valid: false, error: tr("La imagen excede {0} MB (es {1} MB)", imageMaxMB, sizeMB.toFixed(1)) };
  if (vid && videoMaxMB && sizeMB > videoMaxMB) return { valid: false, error: tr("El video excede {0} MB (es {1} MB)", videoMaxMB, sizeMB.toFixed(1)) };

  return { valid: true, error: null, kind: img ? 'image' : 'video', sizeMB };
};

// Lee un File como dataURL (base64). Útil para preview y persistencia mock.
export const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error(tr("No se pudo leer el archivo")));
  reader.readAsDataURL(file);
});

// Extrae duración + dimensiones + un thumbnail (frame del segundo 0.5) de un video.
export const extractVideoMetadata = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;
  video.src = url;

  const cleanup = () => { try { URL.revokeObjectURL(url); } catch { /* noop */ } };

  video.onloadedmetadata = () => {
    const meta = {
      duration: video.duration,
      width: video.videoWidth,
      height: video.videoHeight
    };
    // Captura un frame para thumbnail
    video.currentTime = Math.min(0.5, (video.duration || 1) / 2);
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = Math.min(640, meta.width || 640);
        canvas.height = canvas.width * ((meta.height || 360) / (meta.width || 640));
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.7);
        cleanup();
        resolve({ ...meta, thumbnailDataUrl });
      } catch {
        cleanup();
        resolve({ ...meta, thumbnailDataUrl: null });
      }
    };
  };
  video.onerror = () => { cleanup(); reject(new Error(tr("No se pudo leer la metadata del video"))); };
});

// Comprime una imagen a max=maxDim (lado largo) y calidad jpeg=0.85 si supera maxKB.
// Devuelve dataURL — siempre. Si no necesita compresión, devuelve el dataURL original.
export const compressImage = async (file, { maxDim = 1600, qualityJpeg = 0.85, alwaysCompress = false } = {}) => {
  const dataUrl = await readAsDataUrl(file);
  // Si la imagen es chica y no se fuerza, devolver tal cual
  if (!alwaysCompress && file.size < 800 * 1024) return dataUrl;

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * ratio);
      const h = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      // PNG mantiene transparencia, JPEG es más liviano
      const out = file.type === 'image/png'
        ? canvas.toDataURL('image/png')
        : canvas.toDataURL('image/jpeg', qualityJpeg);
      resolve(out);
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
};

// Lee imagen para obtener dimensiones (sin compresión)
export const extractImageMetadata = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    resolve({ width: img.width, height: img.height });
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve({ width: 0, height: 0 }); };
  img.src = url;
});

// Detecta si el dispositivo tiene cámara móvil (heurística user-agent)
export const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
};
